import { useMemo } from 'react'

import { clsx } from 'clsx'

import { isExactlyOneEmoji } from '@bettermode/common/utils'
import { Message } from '@tribeplatform/gql-client/types'
import { PendingMessage } from '@tribeplatform/react-sdk/hooks'

import { HtmlContent } from '../../../HtmlContent/HtmlContent.js'
import { useI18n } from '../../../i18n/index.js'
import { isLegacyMessage, isPendingMessage } from '../utils/chat.utils.js'

import { MessageAttachments } from './MessageAttachments.js'
import { MessageEmbeds } from './MessageEmbeds.js'
import { Timestamp } from './Timestamp.js'

interface Props {
  message: Message | PendingMessage
  className?: string
  layout?: 'default' | 'compact'
}

export const MessageRenderer = ({
  message,
  className,
  layout = 'default',
}: Props) => {
  const isPending = isPendingMessage(message)
  const isEmpty = !message.content || message.content === '<p></p>'
  const hasEmbeds = message.embeds?.length > 0
  const hasContent = !isEmpty || hasEmbeds
  const fontSize = useMemo(
    () => (isExactlyOneEmoji(message.content) ? '2rem' : undefined),
    [message.content],
  )
  const { $t } = useI18n()
  // Legacy messages have their attachments and embeds in the html content
  // to void duplication, we do not render them separately.
  const isLegacy = isLegacyMessage(message)
  const showTimestamp = layout !== 'compact'
  const isEdited = !isPending && !!message.editedAt
  const editedText = `(${$t({
    id: 'Generics.Edited',
    defaultMessage: 'Edited',
  })})`

  return (
    <div
      className={clsx(className, 'flex flex-col gap-y-2 max-w-fit items-start')}
    >
      {hasContent && (
        <div className="flex flex-col gap-y-2 bg-surface-subdued p-3 rounded-base max-w-full">
          <article
            className="prose prose-mb-sm text-md text-content max-w-fit"
            dir="auto"
            style={{
              fontSize,
            }}
          >
            <HtmlContent
              value={message.content}
              embeds={message?.embeds}
              context="compact"
            />
          </article>
          {!isLegacy && <MessageEmbeds embeds={message.embeds} />}
        </div>
      )}
      {!isLegacy && (
        <MessageAttachments
          attachments={message.attachments}
          images={message.images}
          showAttachmentsCount={hasContent}
          isPending={isPending}
        />
      )}
      {showTimestamp && (
        <div className="flex items-center gap-x-1 w-full -mt-2">
          <Timestamp sentAt={message.sentAt} className="h-full" />
          {isEdited && (
            <div className="font-light text-xs text-content-muted whitespace-nowrap grow lowercase italic">
              {editedText}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
